import React, { useState } from 'react';
import { Modal, Box, SpaceBetween, Button, Alert } from '@amzn/awsui-components-core';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContextProvider';
import { useImpersonation } from '../context/ImpersonationContext';
import { ENVIRONMENT_VARIABLES } from '../../constants/EnvironmentConstants';
import { EmployeeSearchBar } from '../das-finsuite/ea-huddle-v2/components/attendees/EmployeeSearchBar';
import { EmployeeListItem } from '../das-finsuite/ea-huddle-v2/components/attendees/EmployeeListItem';
import { Employee, AttendeePriority } from '../das-finsuite/ea-huddle-v2/interfaces/interface';

/** Routes where the impersonation button is visible. */
const IMPERSONATION_ROUTES = ['/ea-huddle'];

/** Convert "Last, First" or "First Last" employee_name to "First Last". */
const formatDisplayName = (employee: Employee): string => {
  const name = employee.employee_name || employee.login_name;
  if (name.includes(',')) {
    const [last, first] = name.split(',').map((s) => s.trim());
    return `${first} ${last}`;
  }
  return name;
};

export const ImpersonationButton: React.FC = () => {
  const { isAdmin } = useAuth();
  const { pathname } = useLocation();
  const { impersonation, setImpersonation, clearImpersonation } = useImpersonation();

  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const canImpersonate = isAdmin && ENVIRONMENT_VARIABLES.env.Stage !== 'prod';
  const isOnAllowedRoute = IMPERSONATION_ROUTES.some((route) => pathname.startsWith(route));

  if (!canImpersonate || !isOnAllowedRoute) return null;

  const handleEmployeeSelected = (employee: Employee, _priority: AttendeePriority) => {
    setSelectedEmployee(employee);
  };

  const handleApply = () => {
    if (selectedEmployee) {
      setImpersonation({
        alias: selectedEmployee.login_name,
        displayName: formatDisplayName(selectedEmployee)
      });
    }
    setShowModal(false);
    setSelectedEmployee(null);
  };

  const handleOpen = () => {
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const handleDismiss = () => {
    setSelectedEmployee(null);
    setShowModal(false);
  };

  const handleClear = () => {
    clearImpersonation();
    setSelectedEmployee(null);
    setShowModal(false);
  };

  return (
    <>
      <Button variant="icon" iconName="security" ariaLabel="Impersonation" onClick={handleOpen} />

      {showModal && (
        <Modal
          visible={true}
          onDismiss={handleDismiss}
          header="Modify User Details"
          size="medium"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {impersonation && <Button onClick={handleClear}>Clear</Button>}
                <Button variant="link" onClick={handleDismiss}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button variant="primary" onClick={handleApply} disabled={!selectedEmployee}>
                    Apply
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            {impersonation && (
              <Alert type="info">
                Currently impersonating <strong>{impersonation.alias}</strong> ({impersonation.displayName}).
              </Alert>
            )}
            <Box fontSize="body-s" color="text-body-secondary">
              Search and select an employee to impersonate:
            </Box>
            <div className="meeting-meta-behalf-modal">
              <EmployeeSearchBar
                onAddAttendee={handleEmployeeSelected}
                existingEmails={new Set()}
                singleAddButton={true}
                addButtonText="Select"
                placeholder="Search for an employee..."
                fullWidth={true}
              />
            </div>
            {selectedEmployee && <EmployeeListItem employee={selectedEmployee} disableHover />}
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};
